 
import React, { ReactNode } from "react";
import { DV2 } from "../theme";
import { Button, useDv2Theme } from "./Ui";

interface FooterProps {
    onPrevious?: () => void;
    previousLabel?: string;
    onNext?: () => void;
    nextLabel?: string;
    nextDisabled?: boolean;
    nextLoading?: boolean;
    /** Élément affiché à gauche du bouton principal (ex. résumé d'erreurs). */
    extra?: ReactNode;
}

interface Props {
    title: string;
    subtitle?: ReactNode;
    /** Flèche de retour à gauche du titre. */
    onBack?: () => void;
    /** Colonne principale. */
    children: ReactNode;
    /** Colonne de droite (web) : rail d'étapes, encarts. */
    aside?: ReactNode;
    /** Bloc compact affiché sous le titre en version mobile (encart élève replié). */
    asideMobile?: ReactNode;
    /** Pied collant Précédent / Suivant. Absent : pas de pied. */
    footer?: FooterProps;
    /** Largeur de la colonne principale sans colonne de droite. */
    narrow?: boolean;
}

/**
 * Gabarit des écrans du parcours : titre avec retour, colonne de contenu,
 * colonne de droite en version web, pied collant. Mobile d'abord : une seule
 * colonne, le rail d'étapes disparaît au profit de « Étape n sur 4 ».
 */
const WizardShell = ({ title, subtitle, onBack, children, aside, asideMobile, footer, narrow = false }: Props) => {
    const { fontFamily, text, primary } = useDv2Theme();
    return (
        <div style={{ fontFamily, color: text }} className="w-full px-4">
            <div className={`mx-auto w-full ${narrow ? "/max-w-3xl" : "/max-w-6xl"} px-4 sm:px-6 py-4 lg:py-8`}>
                <div className={`grid gap-6 lg:gap-8 items-start ${aside ? "lg:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
                    <div className="flex flex-col gap-5 min-w-0">
                        <header className="flex items-center gap-3">
                            {onBack && (
                                <button
                                    type="button"
                                    onClick={onBack}
                                    aria-label="Retour"
                                    style={{ width: 40, height: 40, borderRadius: 12, background: "#fff", border: `1px solid ${text}`, color: primary, fontSize: 22, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 }}
                                >
                                    ‹
                                </button>
                            )}
                            <div className="min-w-0">
                                <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.2 }} className="lg:text-[26px]">{title}</h1>
                                {subtitle && <div style={{ fontSize: 14, color: text, marginTop: 2 }}>{subtitle}</div>}
                            </div>
                        </header>
                        {asideMobile && <div className="lg:hidden">{asideMobile}</div>}
                        {children}
                        {footer && (
                            <div
                                className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 py-3 lg:py-2 lg:border-0 lg:bg-transparent"
                                style={{ background: DV2.pageBg, borderTop: `1px solid ${text}` }}
                            >
                                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        {footer.onPrevious && (
                                            <Button label={footer.previousLabel ?? "Précédent"} variant="secondary" onClick={footer.onPrevious} className="w-full sm:w-auto" />
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        {footer.extra}
                                        {footer.onNext && (
                                            <Button label={footer.nextLabel ?? "Suivant"} onClick={footer.onNext} disabled={footer.nextDisabled} loading={footer.nextLoading} className="w-full sm:w-auto" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {aside && <div className="hidden lg:flex flex-col gap-4">{aside}</div>}
                </div>
            </div>
        </div>
    );
};

export default WizardShell;
